import { useState } from 'react';
import { X, Search, MapPin, Calendar, FileText, Calculator, Check } from 'lucide-react';

export const PORTAL_MODULES = [
  { id: 'view_location', label: 'Carte en direct', desc: 'Position en temps réel des chauffeurs cochés', icon: MapPin, drivers: true },
  { id: 'view_trip_history', label: 'Historique des trajets', desc: 'Trajets passés des chauffeurs cochés', icon: Calendar, drivers: true },
  { id: 'view_reports', label: 'Rapports journaliers', desc: 'Rapports terminés des chauffeurs cochés (lecture)', icon: FileText, drivers: true },
  { id: 'view_accounting', label: 'Comptabilité', desc: 'Dépenses, reçus et factures émises (lecture), pour votre comptable', icon: Calculator, drivers: false },
];

const Row = ({ checked, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors ${checked ? 'border-amber-500/60 bg-amber-500/10' : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'}`}>
    <span className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${checked ? 'bg-amber-500 text-black' : 'border border-slate-600'}`}>
      {checked && <Check size={14} />}
    </span>
    {children}
  </button>
);

const toggle = (list, id) => (list.includes(id) ? list.filter(x => x !== id) : [...list, id]);

export const ClientAccessModal = ({ client, drivers, onClose, onSave }) => {
  const editing = !!client;
  const [clientName, setClientName] = useState(client?.client_name || '');
  const [email, setEmail] = useState('');
  const [modules, setModules] = useState((client?.permissions || ['view_location']).filter(p => PORTAL_MODULES.some(m => m.id === p)));
  const [driverIds, setDriverIds] = useState(client?.driver_ids || []);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const needsDrivers = modules.some(m => PORTAL_MODULES.find(x => x.id === m)?.drivers);
  const shown = drivers.filter(d => `${d.first_name} ${d.last_name} ${d.email || ''}`.toLowerCase().includes(search.toLowerCase()));
  const valid = modules.length > 0 && (!needsDrivers || driverIds.length > 0) && (editing || (clientName.trim() && email.trim()));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { permissions: modules, driver_ids: needsDrivers ? driverIds : [] };
    await onSave(editing ? body : { ...body, client_name: clientName.trim(), email: email.trim() });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="client-access-modal">
      <form onSubmit={submit} className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between p-5 border-b border-slate-800">
          <div>
            <h2 className="text-lg font-bold text-white uppercase">{editing ? `Accès de ${client.client_name}` : 'Inviter un client'}</h2>
            <p className="text-sm text-slate-400">{editing ? 'Cochez ce que ce client peut voir' : 'Le client recevra un lien pour créer son compte'}</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="client-access-close"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto">
          {!editing && (
            <div className="space-y-3">
              <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Nom du client / entreprise (ex. Transport XYZ inc.)"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500" data-testid="invite-client-name" />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Courriel (ex. client@exemple.com)"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500" data-testid="invite-client-email" />
            </div>
          )}

          <div>
            <p className="text-sm text-slate-300 font-semibold mb-2">Modules visibles</p>
            <div className="space-y-2">
              {PORTAL_MODULES.map(m => (
                <Row key={m.id} checked={modules.includes(m.id)} onClick={() => setModules(toggle(modules, m.id))} testId={`module-${m.id}`}>
                  <m.icon size={16} className="text-amber-500 shrink-0" />
                  <span><span className="block text-sm text-white">{m.label}</span><span className="block text-xs text-slate-400">{m.desc}</span></span>
                </Row>
              ))}
            </div>
          </div>

          {needsDrivers && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-slate-300 font-semibold" data-testid="drivers-count">Chauffeurs visibles ({driverIds.length} coché{driverIds.length > 1 ? 's' : ''})</p>
                <button type="button" onClick={() => setDriverIds(driverIds.length === drivers.length ? [] : drivers.map(d => d.id))}
                  className="text-xs text-amber-400 hover:text-amber-300" data-testid="drivers-toggle-all">
                  {drivers.length > 0 && driverIds.length === drivers.length ? 'Tout décocher' : 'Tout cocher'}
                </button>
              </div>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un chauffeur"
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500" data-testid="drivers-search" />
              </div>
              <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                {shown.map(d => (
                  <Row key={d.id} checked={driverIds.includes(d.id)} onClick={() => setDriverIds(toggle(driverIds, d.id))} testId={`driver-check-${d.id}`}>
                    <span className="text-sm text-white">{d.first_name} {d.last_name}</span>
                    {d.email && <span className="text-xs text-slate-500 truncate ml-auto">{d.email}</span>}
                  </Row>
                ))}
                {!shown.length && <p className="text-xs text-slate-500 py-3 text-center">Aucun chauffeur trouvé</p>}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 p-5 border-t border-slate-800">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg bg-slate-800 text-slate-200 hover:bg-slate-700">Annuler</button>
          <button type="submit" disabled={!valid || saving} className="flex-1 py-2.5 rounded-lg bg-amber-500 text-black font-semibold disabled:opacity-40" data-testid="client-access-save">
            {editing ? 'Enregistrer' : "Créer l'invitation"}
          </button>
        </div>
      </form>
    </div>
  );
};
